import type { EnvironmentId, PullRequestRef } from "@cmmd-forge/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { pullRequestCommandMutationOptions } from "~/lib/pullRequestReactQuery";

type PullRequestCommand = () => Promise<void>;

export interface PullRequestSerializedCommand {
  readonly error: Error | null;
  readonly isPending: boolean;
  execute(command: PullRequestCommand): Promise<void>;
  mutate(command: PullRequestCommand, onSuccess?: () => void): void;
}

export function usePullRequestSerializedCommand(
  environmentId: EnvironmentId,
  reference: PullRequestRef,
): PullRequestSerializedCommand {
  const queryClient = useQueryClient();
  const pendingCommand = useRef<PullRequestCommand | null>(null);
  const commandIsRunning = useRef(false);
  const mutation = useMutation(
    pullRequestCommandMutationOptions({
      environmentId,
      reference,
      queryClient,
      command: () => runSelectedCommand(pendingCommand.current),
    }),
  );

  const execute = async (command: PullRequestCommand) => {
    if (commandIsRunning.current) return;
    commandIsRunning.current = true;
    pendingCommand.current = command;
    try {
      await mutation.mutateAsync();
    } finally {
      pendingCommand.current = null;
      commandIsRunning.current = false;
    }
  };

  return {
    error: mutation.error,
    isPending: mutation.isPending,
    execute,
    mutate: (command, onSuccess) => {
      if (commandIsRunning.current) return;
      void execute(command).then(onSuccess, ignorePullRequestCommandError);
    },
  };
}

async function runSelectedCommand(command: PullRequestCommand | null): Promise<void> {
  if (command === null) throw new Error("No pull request command was selected.");
  await command();
}

function ignorePullRequestCommandError(): void {}
